import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import {
  TextInput,
  StyleSheet,
  View,
  TouchableOpacity,
  Keyboard,
  Platform,
} from "react-native";

import {
  useDebounce,
  isWeb,
  navigate,
  getElementModel,
} from "../../../../helpers/utils";

import ClearSearchIcon from "./icons/ClearSearch";
import SendIcon from "./icons/SendIcon";
import AddIcon from "../icons/elements/AddIcon";
import AppTheme from "../../AppTheme";
import appPages from "../../../../appPages";
import appStyles from "../../../appStyles";
import ManaButton from "../ManaButton";

const Search = React.forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    clearSearch,
    focus,
  }));

  const {
    controller = {},
    filterResults = () => {},
    placeholder = "Search Elements",
    style = {},
    inputStyle = {},
    buttonOptions,
    searchString,
    highlight = false,
    disableBorder = false,
    isChatSearch = false,
    autoFocus = isWeb,
    onFocus,
    showIcon = true,
  } = props;

  const [searchTerm, setSearchTerm] = useState("");
  const [userHasTyped, setUserHasTyped] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const [borderColor, setBorderColor] = useState(AppTheme.DEFAULT_COLOR);
  const inputRef = useRef();

  const buttons = buttonOptions
    ? Array.isArray(buttonOptions)
      ? buttonOptions
      : [buttonOptions]
    : null;

  useEffect(() => {
    if (autoFocus && isWeb && inputRef?.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (isChatSearch) return;

    if (userHasTyped) {
      filterResults({ term: searchTerm, showFavorites: true });
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (onFocus) return;

    setSearchTerm(searchString);
  }, [searchString]);

  // ------------------------------------- METHODS -------------------------------------
  const focus = () => {
    if (inputRef?.current) inputRef.current.focus();
  };

  const clearSearch = () => {
    Keyboard.dismiss();
    if (isWeb && inputRef?.current) {
      inputRef.current.focus();
    }

    if (!searchTerm) return;
    setSearchTerm("");
    filterResults({ term: "" });
  };

  const search = () => {
    filterResults({ term: searchTerm });

    if (isChatSearch) {
      setSearchTerm("");
    }
  };

  const updateSearchTerm = (text) => {
    setUserHasTyped(true);
    setSearchTerm(text);
  };

  const onBlur = () => {
    // Expand integrations when kb is closed
    if (props.onBlur) props.onBlur();

    if (!disableBorder) {
      setBorderColor("#ABADB3");
    }
  };

  const onInputFocus = () => {
    // Collapse integrations when kb is open
    if (props.onInputFocus) props.onInputFocus();

    if (!disableBorder) {
      setBorderColor(AppTheme.DEFAULT_COLOR);
    }
  };

  const goToEditElement = () => {
    navigate(controller, appPages.editElement, "changePage", {
      element: getElementModel({}),
    });
  };

  return (
    <View
      style={[
        styles.searchContainer,
        props.style,
        highlight ? styles.highlight : {},
      ]}
    >
      {/* {advancedFiltering ? (
        <SearchFilters
          isPanelActive={isPanelActive}
          closeFilters={closeFilters}
          filterResults={advancedFiltering}
          updateHasFilters={(has) => setHasFilters(has)}
          sets={sets}
        />
      ) : null} */}

      <View style={[styles.container, style]}>
        {showIcon ? (
          <View style={styles.icon}>
            <AddIcon
              fillColor={AppTheme.DEFAULT_COLOR}
              onPress={goToEditElement}
            />
          </View>
        ) : null}
        {onFocus ? (
          /* Tutorials autocomplete with searchString */
          <TextInput
            ref={inputRef}
            editable={!isWeb}
            name={"searchEdit"}
            style={[
              styles.input,
              { textTransform: "capitalize" },
              showIcon ? { paddingLeft: 46 } : {},
            ]}
            placeholderTextColor={AppTheme.DEFAULT_COLOR}
            placeholder={placeholder}
            onFocus={onFocus}
            defaultValue={searchString}
          />
        ) : (
          <TextInput
            ref={inputRef}
            autoFocus={autoFocus}
            name={"searchEdit"}
            style={[
              styles.input,
              inputStyle,
              showIcon ? { paddingLeft: 46 } : {},
              !disableBorder && { borderColor },
            ]}
            placeholderTextColor={
              isChatSearch ? AppTheme.almostBlack : AppTheme.DEFAULT_COLOR
            }
            onChangeText={(text) => updateSearchTerm(text)}
            onSubmitEditing={search}
            placeholder={placeholder}
            value={searchTerm || ""}
            onBlur={onBlur}
            onFocus={onInputFocus}
          />
        )}

        {isChatSearch ? (
          <TouchableOpacity style={styles.close} onPress={search}>
            <SendIcon color={searchTerm ? AppTheme.DEFAULT_COLOR : ""} />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={styles.close} onPress={clearSearch}>
            <ClearSearchIcon />
          </TouchableOpacity>
        )}
      </View>

      {buttons ? (
        <View
          style={[
            appStyles.horizontalButtons,
            {
              marginHorizontal: "2%",
            },
          ]}
        >
          {buttons.map((bo, index) => (
            <ManaButton key={index} style={{ marginTop: 4 }} {...bo} />
          ))}
        </View>
      ) : null}
    </View>
  );
});

export default Search;

const styles = StyleSheet.create({
  searchContainer: {
    /* position: "absolute",
    bottom: 0,
    left: 0, */
    height: "auto",
    width: "100%",
  },
  highlight: {
    backgroundColor: "#F8E585",
    borderRadius: 16,
    paddingVertical: 5,
  },
  container: {
    height: 40,
    zIndex: 5,
    width: "96%",
    marginHorizontal: "2%",
    borderRadius: 4,
    shadowOffset: { height: 4, width: 0 },
    shadowRadius: 5,
    elevation: 3,
    shadowOpacity: 0.9,
    shadowColor: "rgba(112, 136, 210, 0.1)",
    backgroundColor: "#FFF",
  },
  input: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: "#2D3142",
    width: "100%",
    height: "100%",
    paddingHorizontal: isWeb ? 10 : 20,
    borderRadius: 4,
    backgroundColor: "#F3F2F1",
    borderWidth: 0.5,
    borderColor: "#ABADB3",
  },
  icon: {
    position: "absolute",
    left: 10,
    top: 15,
    bottom: 15,
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  close: {
    position: "absolute",
    right: 0,
    top: 0,
    width: 40,
    height: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});
